import { useState, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { AnalysisResult } from '../lib/api';
import './TrackSearch.css';

interface TrackSearchProps {
  onSelect?: (analysis: AnalysisResult) => void;
  placeholder?: string;
}

export function TrackSearch({ onSelect, placeholder = 'Search tracks…' }: TrackSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (query.trim()) {
      setIsLoading(true);
      const timeoutId = setTimeout(async () => {
        try {
          setResults([]);
          setIsOpen(true);
        } catch (error) {
          console.error('Search failed:', error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="track-search-wrapper" ref={searchRef}>
      <div className={`track-search-input-wrapper ${isFocused ? 'focused' : ''}`}>
        <Search size={16} strokeWidth={1.8} className="search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="track-search-input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            if (query.trim()) setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
        />
        {isLoading ? (
          <Loader2 size={14} strokeWidth={2} className="search-spinner" />
        ) : (
          <kbd className="search-kbd" aria-hidden="true">
            <span className="kbd-key">⌘</span>
            <span className="kbd-key">K</span>
          </kbd>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="track-search-results">
          {results.map((result) => (
            <div
              key={result.id}
              className="track-search-result-item"
              onClick={() => {
                if (onSelect) onSelect(result);
                setQuery('');
                setIsOpen(false);
              }}
            >
              <div className="result-track-name">{result.filename}</div>
              <div className="result-meta">
                {result.tempo && <span>{result.tempo.toFixed(1)} BPM</span>}
                {result.key && <span>{result.key}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
